<template>
  <form
    ref="form"
    class="gate-form"
    :class="{
      'gate-form--invalid': hasError,
      'gate-form--2026': designVersion === '2026'
    }"
    @submit.prevent="handleSubmit">
    <div v-if="designVersion !== '2026'" class="title">
      Wpisz datę
      <br class="only-mobile" />urodzin
    </div>

    <div class="inputs-wrapper">
      <div class="inputs-container">
        <!-- Day -->
        <div class="input-wrapper">
          <label class="input-wrapper__label" for="birth-day">Dzień</label>
          <input
            id="birth-day"
            ref="day"
            :placeholder="placeholderDay"
            class="input-wrapper__input"
            type="number"
            name="day"
            data-max-chars="2"
          />
        </div>
        <!-- Month -->
        <div class="input-wrapper input-wrapper--month">
          <label class="input-wrapper__label" for="birth-month">Miesiąc</label>
          <input
            id="birth-month"
            ref="month"
            :placeholder="placeholderMonth"
            class="input-wrapper__input"
            type="number"
            name="month"
            data-max-chars="2"
          />
        </div>
        <!-- Year -->
        <div class="input-wrapper input-wrapper--year">
          <label class="input-wrapper__label" for="birth-year">Rok</label>
          <input
            id="birth-year"
            ref="year"
            :placeholder="placeholderYear"
            class="input-wrapper__input"
            type="number"
            name="year"
            data-max-chars="4" />
        </div>
      </div>
      <!-- Errors -->
      <transition name="fade">
        <div ref="error" class="error hidden" />
      </transition>
    </div>

    <div>
      <button
        v-if="designVersion === 'old'"
        ref="submit"
        class="btn-gate"
        type="submit"
      >
        Wejdź na stronę
      </button>
      <button v-else ref="submit" class="btn-gate" type="submit">
        <span class="btn-chevron">></span>
        <span class="btn-text">WEJDŹ</span>
      </button>
    </div>
  </form>
</template>

<script>
export default {
  name: 'BirthDateForm',
  props: {
    designVersion: {
      type: String,
      default: 'old',
      validator: value => ['old', '2026'].includes(value)
    }
  },
  data() {
    return {
      hasError: false
    }
  },
  computed: {
    placeholderDay() {
      return this.designVersion === '2026' ? 'dd' : 'DD'
    },
    placeholderMonth() {
      return this.designVersion === '2026' ? 'mm' : 'MM'
    },
    placeholderYear() {
      return this.designVersion === '2026' ? 'rrrr' : 'RRRR'
    }
  },
  async mounted() {
    await this.$nextTick()

    const { day, month, year, form } = this.$refs

    if (day && month && year) {
      day.addEventListener('input', this.toggleInputs)
      month.addEventListener('input', this.toggleInputs)
      year.addEventListener('input', this.toggleInputs)
    }

    if (form) {
      form.addEventListener('submit', this.handleSubmit)
    }

    this.$once('hook:beforeDestroy', () => {
      if (day) day.removeEventListener('input', this.toggleInputs)
      if (month) month.removeEventListener('input', this.toggleInputs)
      if (year) year.removeEventListener('input', this.toggleInputs)
      if (form) form.removeEventListener('submit', this.handleSubmit)
    })
  },
  methods: {
    toggleInputs(evt) {
      const { month, year, submit } = this.$refs
      const input = evt.target
      const maxChars = Number(input.getAttribute('data-max-chars') || Infinity)

      input.value = input.value.slice(0, maxChars)

      if (input === year && input.value.length >= maxChars) {
        submit.focus()
        return
      }

      if (input.value.length >= maxChars) {
        switch (input.name) {
          case 'day':
            month.focus()
            break
          case 'month':
            year.focus()
            break
        }
      }
    },

    getFormData() {
      const { day, month, year } = this.$refs
      return {
        day: day.value,
        month: month.value,
        year: year.value
      }
    },

    showError(message) {
      const { error } = this.$refs
      if (error) {
        error.innerText = message
        if (message) {
          error.classList.remove('hidden')
          this.hasError = true
        } else {
          error.classList.add('hidden')
          this.hasError = false
        }
      }
      return message
    },

    validateDate({ day, month, year }) {
      const isValidDate = date =>
        /^(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])(19|20)\d\d$/.test(date)

      const formattedDate = `${month.padStart(2, '0')}${day.padStart(
        2,
        '0'
      )}${year}`
      const userDate = new Date(year, month - 1, day).getTime()
      const minDate = new Date().setFullYear(new Date().getFullYear() - 18)

      if (!isValidDate(formattedDate) || year < 1910) {
        const errorMsg = 'Wpisz poprawną datę urodzenia'
        this.showError(errorMsg)
        this.$emit('error', errorMsg)
        return false
      } else if (userDate > minDate) {
        const errorMsg =
          'Strona dostępna jest tylko dla użytkowników pełnoletnich'
        this.showError(errorMsg)
        this.$emit('error', errorMsg)
        return false
      }

      this.showError('')
      return true
    },

    validate() {
      const formData = this.getFormData()

      if (this.validateDate(formData)) {
        this.$emit('valid', formData)
        return true
      }

      return false
    },

    handleSubmit(evt) {
      evt.preventDefault()
      evt.target.blur()
      this.validate()
    }
  }
}
</script>

<style lang="scss" scoped>
$gate-background-color: #b81821;
$text-color: #fff;

.gate-form {
  @include center-content;
  flex-direction: column;
  width: 100%;

  @include breakpoint(large) {
    margin: auto 0;
  }

  &--2026 {
    .inputs-wrapper {
      background: #d92a27;

      .inputs-container {
        display: flex;
        gap: 1rem;
      }

      .input-wrapper {
        &--month {
          margin-top: 0;
        }

        &--year {
          margin-top: 0;
        }

        &__label {
          padding-left: 0;
          font-weight: 900;
          font-size: 1rem;
          text-align: center;
        }

        &__input {
          width: 100%;
          min-width: 60px;
          max-width: 86px;
          text-align: center;
          padding: 16px;
          height: 56px;
          font-size: 21px;

          &:focus {
            outline: 1px solid white;
          }

          &::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }
        }
      }
    }

    .error {
      color: #fff;
      font-weight: 900;
    }

    .btn-gate {
      display: flex;
      align-items: center;
      background: transparent;
      border: 1px solid #fff;
      color: $text-color;
      padding: 0;
      width: auto;
      font-size: 16px;
      font-weight: 900;
      height: 53px;

      .btn-chevron {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 100%;
        border-right: 1px solid #fff;
        font-size: 16px;
        font-weight: bold;
      }

      .btn-text {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        text-shadow: none;
      }
    }
  }
}

.title {
  text-align: center;
  font-size: 36px;
  line-height: 1.22;
  font-family: $bold-font;
  text-transform: uppercase;
  margin-top: 45px;
  max-width: 230px;

  @include breakpoint(medium) {
    margin-top: 110px;
    font-size: 42px;
    max-width: none;
  }

  @include breakpoint(large) {
    margin-top: 47px;
    font-size: 46px;
  }
}

.inputs-wrapper {
  @include center-content;
  flex-direction: column;
}

.inputs-container {
  @include breakpoint(medium) {
    @include center-content;
  }
}

.input-wrapper {
  display: inline-flex;
  flex-direction: column;
  width: fit-content;

  &--month,
  &--year {
    margin-top: 26px;

    @include breakpoint(medium) {
      margin-top: 0;
    }
  }

  &__input {
    @include reset-input(number);

    color: $text-color;
    border: 1px solid #fff;
    background: transparent;
    height: 36px;
    width: 120px;
    padding: 12.5px 25px;
    font-size: 16px;

    @include breakpoint(medium) {
      padding: 13px 10px;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &__label {
    padding-left: 15px;
    text-transform: uppercase;
    font-size: 12px;
    margin-bottom: 9px;
    font-family: $regular-font;

    @include breakpoint(medium) {
      padding-left: 10px;
    }
  }
}

.btn-gate {
  @include reset-button;
  @include center-content;

  background: #fff;
  text-transform: uppercase;
  font-family: $regular-font;
  color: $gate-background-color;
  height: 37px;
  width: 200px;
  outline: none;
  font-size: 12px;
  transition: background 0.3s, text-shadow 0.3s;

  &:focus {
    outline: 2px solid white;
  }

  &:hover {
    text-shadow: 0px 0px 1px;
    background: darken($color: #fff, $amount: 5%);
  }
}

.error {
  margin-top: 20px;
  font-family: $light-font;
  font-size: 12px;

  @include breakpoint(medium) {
    width: 100%;
    text-align: center;
  }

  @include breakpoint(large) {
    font-size: 18px;
  }

  &.hidden {
    display: none;
  }
}

.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.only-mobile {
  @include breakpoint(medium) {
    display: none;
  }
}
</style>
